import { ProductsModel } from './products.model';
import { ProductItem } from './products.types';

export class ProductsController {
  
  /**
   * Returns list of all products, optionally filtered by category and search term.
   */
  public static list(categoryFilter: string = 'Todas', search: string = ''): ProductItem[] {
    const all = ProductsModel.getProducts();
    const query = search.trim().toLowerCase();

    return all.filter(p => {
      const matchesCat = categoryFilter === 'Todas' || p.category.toLowerCase() === categoryFilter.toLowerCase();
      const matchesSearch = !query || 
                            p.name.toLowerCase().includes(query) || 
                            p.id.toLowerCase().includes(query) || 
                            p.model.toLowerCase().includes(query);
      return matchesCat && matchesSearch;
    });
  }

  /**
   * Returns list of all products from server MySQL real database.
   */
  public static async listFromServer(categoryFilter: string = 'Todas', search: string = ''): Promise<ProductItem[]> {
    const all = await ProductsModel.fetchProducts();
    const query = search.trim().toLowerCase();

    return all.filter(p => {
      const matchesCat = categoryFilter === 'Todas' || p.category.toLowerCase() === categoryFilter.toLowerCase();
      const matchesSearch = !query || 
                            p.name.toLowerCase().includes(query) || 
                            p.id.toLowerCase().includes(query) || 
                            p.model.toLowerCase().includes(query);
      return matchesCat && matchesSearch;
    });
  }

  /**
   * Retrieves products belonging strictly to the "Acessórios" category.
   */
  public static getAccessoryProducts(): ProductItem[] {
    return ProductsModel.getProducts().filter(p => {
      const cat = p.category.toLowerCase();
      return cat === 'acessórios' || cat === 'acessorios';
    });
  }

  /**
   * Retrieves products belonging strictly to the "Acessórios" category from server.
   */
  public static async getAccessoryProductsFromServer(): Promise<ProductItem[]> {
    const all = await ProductsModel.fetchProducts();
    return all.filter(p => {
      const cat = p.category.toLowerCase();
      return cat === 'acessórios' || cat === 'acessorios';
    });
  }

  /**
   * Retrieves products belonging strictly to the "Partes" category.
   */
  public static getPartProducts(): ProductItem[] {
    return ProductsModel.getProducts().filter(p => {
      const cat = p.category.toLowerCase();
      return cat === 'partes' || cat === 'peças' || cat === 'acessórios' || cat === 'acessorios';
    });
  }

  /**
   * Retrieves products belonging strictly to the "Partes" category from server.
   */
  public static async getPartProductsFromServer(): Promise<ProductItem[]> {
    const all = await ProductsModel.fetchProducts();
    return all.filter(p => {
      const cat = p.category.toLowerCase();
      return cat === 'partes' || cat === 'peças' || cat === 'acessórios' || cat === 'acessorios';
    });
  }

  /**
   * Validates image size limit (550 KB)
   * 550 * 1024 = 563200 bytes
   */
  private static MAX_IMAGE_SIZE_BYTES = 550 * 1024;

  public static validateImageSize(file: File | null): { valid: boolean; reason?: string } {
    if (!file) return { valid: true };
    if (file.size > this.MAX_IMAGE_SIZE_BYTES) {
      const sizeInKb = (file.size / 1024).toFixed(1);
      return { 
        valid: false, 
        reason: `A imagem excede o limite estrito do sistema de 550 KB (Tamanho carregado: ${sizeInKb} KB). Reduza o tamanho ou escolha outra imagem.` 
      };
    }
    return { valid: true };
  }

  /**
   * Handles saves (Create/Update) of a product item using native FormData payload
   */
  public static handleSave(
    id: string | null, 
    formData: FormData, 
    extra: { 
      accessories: string[]; 
      pieces: string[]; 
      productImageBase64: string;
      projectImage1Base64: string;
      projectImage2Base64: string;
    }
  ): { success: boolean; error?: string; product?: ProductItem } {
    
    const name = formData.get('name') as string || '';
    if (!name.trim()) {
      return { success: false, error: 'O nome do produto é obrigatório.' };
    }

    const category = formData.get('category') as string || 'Coifas';
    const model = formData.get('model') as string || '';
    const desc = formData.get('desc') as string || '';
    const erpProductId = ((formData.get('erpProductId') as string) || (formData.get('erp_product_id') as string) || '').trim();

    // Collect checked production steps by evaluating form checkboxes dynamically
    const activeSteps: string[] = [];
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('sequence_') && (value === 'on' || value === 'true' || value === '1')) {
        const stepName = key.replace('sequence_', '').trim();
        if (stepName && !activeSteps.includes(stepName)) {
          activeSteps.push(stepName);
        }
      }
    }

    const isNew = !id;
    let finalId = id || '';
    let oldProduct: ProductItem | null = null;
    
    if (isNew) {
      // Find maximum numeric ID to generate sequential key
      const currentList = ProductsModel.getProducts();
      const numericalIds = currentList
        .map(p => parseInt(p.id, 10))
        .filter(n => !isNaN(n));
      const maxIdNum = numericalIds.length > 0 ? Math.max(...numericalIds) : 0;
      finalId = String(maxIdNum + 1).padStart(3, '0');
    } else {
      const existing = ProductsModel.getProducts().find(p => p.id === id);
      if (existing) {
        oldProduct = existing;
      }
    }

    const savedProductItem: ProductItem = {
      id: finalId,
      erpProductId: erpProductId || undefined,
      name: name.trim(),
      category,
      model: model.trim(),
      desc: desc.trim(),
      // Use exact Base64 values (either new, existing, or empty string if cleared)
      productImage: extra.productImageBase64,
      projectImage1: extra.projectImage1Base64,
      projectImage2: extra.projectImage2Base64,
      accessories: extra.accessories,
      pieces: extra.pieces,
      productionSequence: activeSteps.length > 0 ? activeSteps : ['Corte', 'Dobra', 'Solda', 'Embalagem']
    };

    try {
      ProductsModel.saveWithImageCleanup(oldProduct, savedProductItem);
      return { success: true, product: savedProductItem };
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro inesperado ao salvar o produto.' };
    }
  }

  /**
   * Handles saves (Create/Update) of a product item to database on server PHP
   */
  public static async handleSaveToServer(
    id: string | null, 
    formData: FormData, 
    extra: { 
      accessories: string[]; 
      pieces: string[]; 
      productImageBase64: string; 
      projectImage1Base64: string; 
      projectImage2Base64: string; 
    }
  ): Promise<{ success: boolean; error?: string; product?: ProductItem }> {
    
    const name = formData.get('name') as string || '';
    if (!name.trim()) {
      return { success: false, error: 'O nome do produto é obrigatório.' };
    }

    const category = formData.get('category') as string || 'Coifas';
    const model = formData.get('model') as string || '';
    const desc = formData.get('desc') as string || '';
    const erpProductId = ((formData.get('erpProductId') as string) || (formData.get('erp_product_id') as string) || '').trim();

    // Collect checked production steps by evaluating form checkboxes dynamically
    const activeSteps: string[] = [];
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('sequence_') && (value === 'on' || value === 'true' || value === '1')) {
        const stepName = key.replace('sequence_', '').trim();
        if (stepName && !activeSteps.includes(stepName)) {
          activeSteps.push(stepName);
        }
      }
    }

    const isNew = !id;
    let finalId = id || '';
    
    if (isNew) {
      const currentList = await ProductsModel.fetchProducts();
      const numericalIds = currentList
        .map(p => parseInt(p.id, 10))
        .filter(n => !isNaN(n));
      const maxIdNum = numericalIds.length > 0 ? Math.max(...numericalIds) : 0;
      finalId = String(maxIdNum + 1).padStart(3, '0');
    }

    const savedProductItem: ProductItem = {
      id: finalId,
      erpProductId: erpProductId || undefined,
      name: name.trim(),
      category,
      model: model.trim(),
      desc: desc.trim(),
      productImage: extra.productImageBase64,
      projectImage1: extra.projectImage1Base64,
      projectImage2: extra.projectImage2Base64,
      accessories: extra.accessories,
      pieces: extra.pieces,
      productionSequence: activeSteps.length > 0 ? activeSteps : ['Corte', 'Dobra', 'Solda', 'Embalagem']
    };

    try {
      const savedOk = await ProductsModel.saveProductToServer(savedProductItem, isNew);
      if (savedOk) {
        return { success: true, product: savedProductItem };
      } else {
        return { success: false, error: 'Erro ao salvar produto no banco de dados real.' };
      }
    } catch (err: any) {
      return { success: false, error: err.message || 'Erro inesperado ao salvar o produto.' };
    }
  }

  /**
   * Removes a product totally and purges associated image blobs
   */
  public static handleDelete(id: string): boolean {
    return ProductsModel.deleteProduct(id);
  }

  /**
   * Removes a product totally from server and local cache
   */
  public static async handleDeleteOnServer(id: string): Promise<{ success: boolean; error?: string }> {
    return await ProductsModel.deleteProductFromServer(id);
  }
}
